import { Link } from "react-router-dom";
import { useEffect, useRef } from "react";

export default function HeroSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-in");
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll(".reveal");
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="relative bg-dark-bg min-h-screen">
      <div className="relative z-10 w-full px-6 md:px-10 lg:px-16 pt-28 md:pt-36 pb-12">
        {/* Upper Content Split */}
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-16 mb-12 lg:mb-16">
          {/* Left: Large Title */}
          <div className="lg:w-1/2 reveal opacity-0 translate-y-6 transition-all duration-700 ease-out">
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-medium text-charcoal-50 leading-[1.05] tracking-tight-xl">
              Water, the way
              <br />
              God intended
            </h1>
          </div>

          {/* Right: Description + Link */}
          <div className="lg:w-1/2 flex flex-col justify-end reveal opacity-0 translate-y-6 transition-all duration-700 ease-out delay-200">
            <div className="border border-gold/30 p-5 md:p-6 rounded-xl mb-6">
              <p className="text-sm md:text-base text-gold font-sans leading-relaxed italic">
                Passionate about removing harmful contaminants from the Midwest water supply, one home at a time.
              </p>
            </div>
            <Link
              to="/packages"
              className="inline-flex items-center gap-2 text-sm font-sans font-semibold text-gold hover:text-gold-light transition-colors duration-300 group whitespace-nowrap"
            >
              Explore our packages
              <i className="ri-arrow-right-line text-lg group-hover:translate-x-1 transition-transform duration-300"></i>
            </Link>
          </div>
        </div>

        {/* Lower Hero Image */}
        <div className="reveal opacity-0 translate-y-6 transition-all duration-700 ease-out delay-300">
          <div className="relative w-full rounded-3xl overflow-hidden aspect-[21/9] md:aspect-[21/8] border border-dark-border">
            <img
              src="https://readdy.ai/api/search-image?query=Luxurious%20modern%20home%20interior%20open%20kitchen%20and%20living%20space%20stainless%20steel%20water%20filtration%20faucet%20pouring%20crystal%20clear%20water%20into%20elegant%20glass%20warm%20ambient%20lighting%20marble%20countertops%20minimalist%20high%20end%20design%20dark%20moody%20atmosphere%20black%20and%20gold%20accents%20dramatic%20professional%20photography&width=1400&height=500&seq=hero1&orientation=landscape"
              alt="Luxury home interior with premium water filtration system"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-dark-bg/60 via-dark-bg/20 to-transparent" />
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-charcoal-400">
        <span className="text-xs font-sans tracking-wide">Scroll</span>
        <div className="w-px h-8 bg-dark-border animate-pulse" />
      </div>
    </section>
  );
}